import React, { ReactElement, useMemo } from 'react';
import { NextApiRequestQuery } from 'next/dist/server/api-utils';
import { PersonBaseInterface } from 'components/interfaces/PersonInterface';
import { useTranslation } from 'utils/localization';
import PersonBase from 'components/pages/person/detail/base/PersonBase';
import { ConfigPageInterface } from 'utils/helpers/server/serverSideProps/config';
import {
    CoachDetailApiInterface,
    CoachMatchListItemApiInterface,
    EntityReferenceApiEnum,
    FieldTypeApiEnum,
} from 'submodules/api_middleware';
import { PaginatorPageInterface } from 'components/interfaces/Paginator';
import { Config_CoachMatches_ApiInterface } from 'submodules/api_middleware/src/interfaces/config/CoachMatchesConfigApiInterface';
import Table, {
    createDetailLinkTh,
    createLinkByType,
    createTd,
    createTh,
} from 'components/tables/Table';
import {
    ColumnHeadInterface,
    ColumnSize,
    ColumnValueInterface,
    ColumnValueWeight,
    CompetitionInterface,
    CompetitionYearInterface,
    FilterSize,
    FiltersInterface,
    LabelSize,
    Option,
} from 'components/interfaces/GeneralInterface';
import { toDate, toTime } from 'utils/helpers/datetime';
import getCoachOverview from 'components/pages/person/detail/base/utils/getCoachOverview';
import { ClubListItemInterface } from 'components/interfaces/ClubInterface';
import { getMatchFilters } from 'components/pages/base/MatchFilters';
import { transformClubsToOptions } from 'utils/helpers/browser/filter';
import {
    getMatchTdColor,
    getMatchTdLabel,
} from 'components/pages/player/detail/MatchesBlock';
import { getRoute } from 'constants/routes';
import { MATCH_FILTER_KEYS } from 'constants/filterKeysConstants';
import { buildIs1MatchReportUrl } from 'utils/helpers/browser/is1Url';

interface Props
    extends PaginatorPageInterface,
        ConfigPageInterface<Config_CoachMatches_ApiInterface> {
    personBase: PersonBaseInterface;
    coach: CoachDetailApiInterface;
    matches: CoachMatchListItemApiInterface[];
    competitionList: CompetitionInterface[];
    competitionYears: CompetitionYearInterface[];
    clubList: ClubListItemInterface[];
    filterInitQuery: NextApiRequestQuery;
}

/**
 * Coach Utkání page. Renders the same shaped grid (search + filter chips +
 * pagination) as the player matches grid, but stays on a slim
 * CoachMatchListItemApiInterface payload so we don't need to reshape into the
 * rich MatchApiInterface tree.
 */
const CoachMatchesPage = ({
    personBase,
    coach,
    matches,
    config,
    paginator,
    competitionList,
    competitionYears,
    clubList,
    filterInitQuery,
}: Props): ReactElement => {
    const { t } = useTranslation();

    const filterConfig: FiltersInterface = useMemo(() => {
        const base = getMatchFilters({
            t,
            clubList,
            competitionList,
            competitionYears,
            filterInitQuery,
        });

        // Coach Utkání keeps a curated set of filters only (per FE spec):
        // Fotbalový ročník, Datum od, Datum do, Soutěž, and a single combined
        // Klub filter (matches when the picked club is on either side of the
        // fixture). All other shared filters — match state, round, both
        // protest checkboxes, the legacy two-sided club filters — are
        // intentionally dropped here. Soutěž and Klub dropdowns are populated
        // SSR-side from coach-scoped endpoints, not the global lists.
        const baseAdvanced = base.advanced ?? [];
        const findByName = (name: string) =>
            baseAdvanced.find((field) => field.name === name);

        // Same NUMBER input as Members "Roční" filter — not a season dropdown.
        const yearField = {
            name: MATCH_FILTER_KEYS.competition_year,
            label: 'MatchesPage.competitionYear',
            type: FieldTypeApiEnum.NUMBER,
            size: FilterSize.S20,
            labelSize: LabelSize.ML,
        };
        const dateFromField = findByName(MATCH_FILTER_KEYS.date_from);
        const dateToField = findByName(MATCH_FILTER_KEYS.date_to);

        // Coach-scoped Soutěž/Klub: replace the global remote searches with
        // local prefix matching against the SSR-supplied coach lists so the
        // dropdown can never expand outside this coach's history (per spec:
        // "Bere se ze soutěží, ve kterých trenér trénoval / klubů, ve kterých
        // trenér působil").
        const localCompetitionSearch = (
            value: NextApiRequestQuery
        ): Promise<Option[]> => {
            const needle = String(value?.query ?? '')
                .trim()
                .toLowerCase();
            const filtered = needle
                ? competitionList.filter((c) => {
                      const name = (c.name ?? '').toLowerCase();
                      const ident = (c.identifier ?? '').toLowerCase();
                      return name.includes(needle) || ident.includes(needle);
                  })
                : competitionList;
            return Promise.resolve(
                filtered.map((c) => ({
                    label: c.identifier
                        ? `${c.name} (${c.identifier})`
                        : c.name,
                    value: c.id,
                }))
            );
        };

        const localClubSearch = (
            value: NextApiRequestQuery
        ): Promise<Option[]> => {
            const needle = String(value?.query ?? '')
                .trim()
                .toLowerCase();
            const filtered = needle
                ? clubList.filter((club) => {
                      const name = (club.name ?? '').toLowerCase();
                      const internal = (club.internal_id ?? '').toLowerCase();
                      return name.includes(needle) || internal.includes(needle);
                  })
                : clubList;
            return Promise.resolve(
                transformClubsToOptions(filtered, 'internal_id')
            );
        };

        const competitionFieldRaw = findByName(MATCH_FILTER_KEYS.leagues);
        let competitionField: typeof competitionFieldRaw | undefined;
        if (competitionFieldRaw) {
            // Drop reInitOnFields — local search no longer depends on the
            // selected year, so we don't want the dropdown to reset on year
            // change.
            const { reInitOnFields: _drop, ...rest } = competitionFieldRaw;
            competitionField = {
                ...rest,
                onSearch: localCompetitionSearch,
                initValuesCb: () => localCompetitionSearch({}),
            };
        }

        const homeClubField = findByName(MATCH_FILTER_KEYS.home_club);
        let teamField: typeof homeClubField | undefined;
        if (homeClubField) {
            const { reInitOnFields: _drop, ...rest } = homeClubField;
            teamField = {
                ...rest,
                name: MATCH_FILTER_KEYS.team_internal_id,
                label: 'MatchesPage.club',
                onSearch: localClubSearch,
                initValuesCb: () => localClubSearch({}),
            };
        }

        const advanced = [
            yearField,
            dateFromField,
            dateToField,
            competitionField,
            teamField,
        ].filter((field): field is NonNullable<typeof field> => !!field);

        return {
            ...base,
            advanced,
        };
    }, [t, clubList, competitionList, competitionYears, filterInitQuery]);

    const columns: ColumnHeadInterface[] = useMemo(
        () => [
            createDetailLinkTh((id) =>
                getRoute('/matches/{0}', [id as string])
            ),
            createTh(t, 'MatchesPage.matchDateTime', ColumnSize.M),
            createTh(t, 'MatchesPage.round', ColumnSize.S),
            createTh(t, 'MatchesPage.homeAwayTeams', ColumnSize.L),
            createTh(t, 'general.result', ColumnSize.M),
            createTh(t, 'MatchesPage.record', ColumnSize.S),
            createTh(t, 'MatchesPage.competition', ColumnSize.L),
        ],
        [t]
    );

    const data: ColumnValueInterface[][] = useMemo(
        () =>
            matches.map((row) => {
                const score =
                    row.home_score != null && row.away_score != null
                        ? `${row.home_score}:${row.away_score}`
                        : '';

                const homeLink =
                    row.home_club_id != null && row.competition_id != null
                        ? createLinkByType(
                              t,
                              {
                                  team_id: row.home_club_id,
                                  club_id: row.home_club_id,
                                  competition_id: row.competition_id,
                                  name: row.home_team ?? '',
                              },
                              EntityReferenceApiEnum.club_competition
                          )
                        : row.home_team ?? '–';

                const awayLink =
                    row.away_club_id != null && row.competition_id != null
                        ? createLinkByType(
                              t,
                              {
                                  team_id: row.away_club_id,
                                  club_id: row.away_club_id,
                                  competition_id: row.competition_id,
                                  name: row.away_team ?? '',
                              },
                              EntityReferenceApiEnum.club_competition
                          )
                        : row.away_team ?? '–';

                const competitionLink =
                    row.competition_id != null
                        ? createLinkByType(
                              t,
                              {
                                  id: row.competition_id,
                                  name: row.competition_name ?? '',
                                  identifier: row.competition_number ?? '',
                              },
                              EntityReferenceApiEnum.competition
                          )
                        : row.competition_name ?? '–';

                return [
                    createTd(row.match_id),
                    createTd([
                        row.kickoff_at
                            ? (toDate(row.kickoff_at) as string)
                            : '',
                        row.kickoff_at
                            ? (toTime(row.kickoff_at) as string)
                            : '',
                    ]),
                    createTd(row.round != null ? String(row.round) : ''),
                    createTd(
                        [homeLink, awayLink],
                        ColumnValueWeight.BOLD_FIRST
                    ),
                    createTd(
                        getMatchTdLabel(t, row.state, score),
                        ColumnValueWeight.BOLD,
                        getMatchTdColor(row.state)
                    ),
                    createTd(
                        {
                            linkLabel: t('MatchesPage.record'),
                            url: buildIs1MatchReportUrl(row.match_publish_id),
                            blankPage: true,
                        },
                        ColumnValueWeight.BOLD
                    ),
                    createTd([competitionLink], ColumnValueWeight.BOLD_FIRST),
                ];
            }),
        [matches, t]
    );

    return (
        <PersonBase
            config={config}
            personBase={personBase}
            pageSubTitle={t('CoachPage.coachMatches')}
            minimizeGradient
            isArchived={!!coach.archived_at}
            overview={getCoachOverview({ t, coach })}
        >
            <Table
                columns={columns}
                data={data}
                cardTitle={t('CoachPage.coachMatches')}
                paginator={paginator}
                noEntriesSubText={t('CoachPage.matches.empty')}
                filterConfig={{
                    ...filterConfig,
                    fixed: {
                        ...filterConfig.fixed,
                        mainField: undefined,
                        onSearch: undefined,
                        showAdvancedBtn: undefined,
                    },
                }}
            />
        </PersonBase>
    );
};

export default CoachMatchesPage;
